import { SkeletonCard } from '@/components/skeleton-card/SkeletonCard';
import ENV from '@/env/env';
import useAccessToken from '@/hooks/useAccessToken';
import { MapEvent, MapMouseEvent, RequestTransformFunction, Map as MapGl, MapTouchEvent } from 'mapbox-gl';
import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import MapGL, { GeolocateControl, Layer, LayerProps, MapRef, Popup, Source, ViewState } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

import { LayerControl } from './LayerControl';
import { MapTopBar } from './MapTopBar';
import { LandownerPopup } from './LandownerPopup';
import { useTheme } from '@/components/theme/useTheme';
import {
	API_SOURCE_PATH,
	GEOMAP_SOURCE_ID,
	getMapStyleUrl,
	getStopsArray,
	LAYERS_UI_CONFIG,
	YES_MAPBOX_SUBSTATIONS_ID,
	YES_MAPBOX_TRANSMISSION_ID
} from './constants';
import usePrevious from '@/hooks/usePrevious';
import { MapStyleControl } from './MapStyleControl';
import { HoverPopup } from './HoverPopup';
import { FeatureResult, Layer as LayerType, LayerUIConfig, MapStyle } from './types';
import { FeatureSearchControl } from './FeatureSearchControl';
import { enumValueFromString } from '@/utils/enums';
import { getIconExpression, getIconSizeExpression, loadMapIcons } from './map-icons';
import { toast } from 'sonner';
import { UnknownLayerPopup } from './UnknownLayerPopup';
import { DevicePopup } from './DevicePopup';
import { PlantPopup } from './PlantPopup';
import useTitle from '@/hooks/useTitle.ts';
import { TtmsPopup } from '@/routes/map/TtmsPopup.tsx';
import { EiaTransmissionPopup } from '@/routes/map/EiaTransmissionPopup.tsx';
import { EiaTransmissionLegend } from '@/routes/map/EiaTransmissionLegend.tsx';
import MapLegend from '@/routes/map/MapLegend.tsx';
import { EiaSubstationsPopup } from '@/routes/map/EiaSubstationsPopup.tsx';
import { Link } from '@/components/link/Link.tsx';
import ViewOnGoogleMaps from '@/routes/map/ViewOnGoogleMaps.tsx';
import { parseUrlParams, SEARCHABLE_LAYERS_CONFIG } from '@/routes/map/Utils.ts';
import { LoadPopup } from '@/routes/map/LoadPopup.tsx';
import { PlannedPlantPopup } from '@/routes/map/PlannedPlantPopup.tsx';

const TILE_URL = window.location.origin + '/api/v1/geomap/{z}/{x}/{y}.pbf';
const EIA_TRANSMISSION_TILE_URL = `https://api.mapbox.com/v4/yesenergy.4x2rjrj4/{z}/{x}/{y}.vector.pbf?access_token=${ENV.YES_MAPBOX_PUBLIC_KEY_TOKEN}`;
const EIA_SUBSTATIONS_TILE_URL = `https://api.mapbox.com/v4/yesenergy.a7a2yic7/{z}/{x}/{y}.vector.pbf?access_token=${ENV.YES_MAPBOX_PUBLIC_KEY_TOKEN}`;

const MAPBOX_TOKEN = ENV.MAPBOX_PUBLIC_KEY;

interface PopupInfo {
	longitude: number;
	latitude: number;
	properties: Record<string, string>;
	layerType?: LayerType;
}

type Position = GeoJSON.Point['coordinates'];

const INITIAL_LNG = -105;
const INITIAL_LAT = 40;
const INITIAL_ZOOM = 10;

export default function Map() {
	useTitle('Map');
	const accessToken = useAccessToken();
	const mapRef = useRef<MapRef>(null);

	const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
	const [hoverInfo, setHoverInfo] = useState<PopupInfo | null>(null);
	const [iconsLoaded, setIconsLoaded] = useState(false);
	const [layers, setLayers] = useState<LayerUIConfig[]>(Object.values(LAYERS_UI_CONFIG));
	const [initialFeatureSelected, setInitialFeatureSelected] = useState(false);
	const [rightClickInfo, setRightClickInfo] = useState<{
		longitude: number;
		latitude: number;
		zoom: number;
	} | null>(null);

	const { theme } = useTheme();

	const debouncedUrlUpdate = useCallback((longitude: number, latitude: number, zoom: number) => {
		const timeoutId = setTimeout(() => {
			const url = new URL(window.location.href);
			url.searchParams.set('lng', longitude.toFixed(6));
			url.searchParams.set('lat', latitude.toFixed(6));
			url.searchParams.set('zoom', zoom.toFixed(1));

			window.history.replaceState({}, '', url.toString());
		}, 500);

		return timeoutId;
	}, []);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const [mapStyle, setMapStyle] = useState<MapStyle>(() => {
		return theme === 'dark' ? 'light' : 'dark';
	});

	const LAYERS: Record<LayerType, LayerProps> = {
		[LayerType.Landowners]: {
			id: LayerType.Landowners,
			type: 'symbol' as const,
			source: GEOMAP_SOURCE_ID,
			'source-layer': LayerType.Landowners,
			layout: {
				'icon-image': getIconExpression(LayerType.Landowners, mapStyle),
				'icon-size': 1,
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		},
		[LayerType.Devices]: {
			id: LayerType.Devices,
			type: 'symbol' as const,
			source: GEOMAP_SOURCE_ID,
			'source-layer': LayerType.Devices,
			layout: {
				'icon-image': getIconExpression(LayerType.Devices, mapStyle),
				'icon-size': 1,
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		},
		[LayerType.Plants]: {
			id: LayerType.Plants,
			type: 'circle',
			source: GEOMAP_SOURCE_ID,
			'source-layer': LayerType.Plants,
			paint: {
				"circle-radius": 5,
				"circle-color": 'green'
			}
		},
		[LayerType.Ttms]: {
			id: LayerType.Ttms,
			type: 'symbol' as const,
			source: GEOMAP_SOURCE_ID,
			'source-layer': LayerType.Ttms,
			layout: {
				'icon-image': getIconExpression(LayerType.Ttms, mapStyle),
				'icon-size': 1,
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		},
		[LayerType.EiaTransmission]: {
			id: LayerType.EiaTransmission,
			type: 'line' as const,
			source: YES_MAPBOX_TRANSMISSION_ID,
			'source-layer': LayerType.EiaTransmission,
			paint: {
				'line-color': {
					property: 'VOLTAGE',
					type: 'interval',
					stops: getStopsArray(LAYERS_UI_CONFIG[LayerType.EiaTransmission].stops)
				},
				'line-width': {
					property: 'VOLTAGE',
					type: 'interval',
					stops: [
						[0, 3],
						[230, 5],
						[500, 7]
					]
				}
			}
		},
		[LayerType.EiaSubstations]: {
			id: LayerType.EiaSubstations,
			type: 'symbol' as const,
			source: YES_MAPBOX_SUBSTATIONS_ID,
			'source-layer': LayerType.EiaSubstations,
			layout: {
				'icon-image': getIconExpression(LayerType.EiaSubstations, mapStyle),
				'icon-size': 1,
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		},
		[LayerType.PlannedPlants]: {
			id: LayerType.PlannedPlants,
			type: 'symbol' as const,
			source: GEOMAP_SOURCE_ID,
			'source-layer': LayerType.PlannedPlants,
			layout: {
				'icon-image': getIconExpression(LayerType.PlannedPlants, mapStyle),
				'icon-size': getIconSizeExpression(LayerType.PlannedPlants, mapStyle),
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		},
		[LayerType.Loads]: {
			id: LayerType.Loads,
			type: 'symbol' as const,
			source: GEOMAP_SOURCE_ID,
			'source-layer': LayerType.Loads,
			layout: {
				'icon-image': getIconExpression(LayerType.Loads, mapStyle),
				'icon-size': getIconSizeExpression(LayerType.Loads, mapStyle),
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		}
	};

	const [viewState, setViewState] = useState<ViewState>(() => {
		const urlParams = parseUrlParams();
		if (urlParams && urlParams.latitude && urlParams.longitude && urlParams.zoom) {
			return {
				longitude: urlParams.longitude,
				latitude: urlParams.latitude,
				zoom: urlParams.zoom,
				pitch: 0,
				padding: {},
				bearing: 0
			};
		}

		return {
			longitude: INITIAL_LNG,
			latitude: INITIAL_LAT,
			zoom: INITIAL_ZOOM,
			pitch: 0,
			padding: {},
			bearing: 0
		};
	});

	const prevTheme = usePrevious(theme);

	const onHandleIdle = () => {
		if (!mapRef.current) return;

		if (!initialFeatureSelected) {
			const map = mapRef.current.getMap();
			const config = SEARCHABLE_LAYERS_CONFIG[LayerType.Devices];
			const sourceFeatures = map.querySourceFeatures(config.sourceId, {
				sourceLayer: config.sourceLayer
			});
			const urlParams = parseUrlParams();

			sourceFeatures.forEach(feature => {
				if (feature.geometry.type === 'Point' && feature.properties) {
					if (feature.properties.id === urlParams?.deviceId) {
						const [longitude, latitude] = (feature.geometry as GeoJSON.Point).coordinates;
						map.flyTo({
							center: [longitude, latitude],
							zoom: 10,
							duration: 1000
						});

						const onMoveEnd = () => {
							map.off('moveend', onMoveEnd);
							if (feature.properties) {
								onFeatureSelect({
									id: feature.properties.id,
									name: feature.properties.name,
									longitude,
									latitude,
									properties: feature.properties,
									layerType: LayerType.Devices
								});
							}
						};

						setInitialFeatureSelected(true);

						map.on('moveend', onMoveEnd);
					}
				}
			});
		}
	};

	const setupTouchListeners = useCallback((map: MapGl) => {
		let longPressTimer: NodeJS.Timeout | null = null;
		let startPos: { x: number; y: number } | null = null;
		let touchLngLat: { lng: number; lat: number } | null = null;
		let isLongPress = false;

		const handleTouchStart = (e: MapTouchEvent) => {
			if (e.point && e.lngLat) {
				startPos = { x: e.point.x, y: e.point.y };
				touchLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
				isLongPress = false;

				longPressTimer = setTimeout(() => {
					if (touchLngLat) {
						isLongPress = true;

						if (mapRef.current) {
							mapRef.current.flyTo({
								center: { lng: touchLngLat.lng, lat: touchLngLat.lat },
								zoom: 16,
								duration: 1000
							});
						}

						setRightClickInfo({
							longitude: touchLngLat.lng,
							latitude: touchLngLat.lat,
							zoom: 16
						});
					}
				}, 500);
			}
		};

		const handleTouchMove = (e: MapTouchEvent) => {
			if (!startPos || !longPressTimer) return;

			if (e.point) {
				const deltaX = Math.abs(e.point.x - startPos.x);
				const deltaY = Math.abs(e.point.y - startPos.y);

				if (deltaX > 10 || deltaY > 10) {
					clearTimeout(longPressTimer);
					longPressTimer = null;
					isLongPress = false;
				}
			}
		};

		const handleTouchEnd = (e: MapTouchEvent) => {
			if (longPressTimer) {
				clearTimeout(longPressTimer);
				longPressTimer = null;
			}

			if (isLongPress && e.originalEvent) {
				e.originalEvent.preventDefault();
			}

			startPos = null;
			touchLngLat = null;
			isLongPress = false;
		};

		map.on('touchstart', handleTouchStart);
		map.on('touchmove', handleTouchMove);
		map.on('touchend', handleTouchEnd);
		map.on('touchcancel', handleTouchEnd);
	}, []);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (prevTheme === theme) {
			return;
		}

		if (mapStyle === 'satellite' || mapStyle === 'standard') {
			return;
		}

		if (theme === 'light') {
			setMapStyle('dark');
		} else if (theme === 'dark') {
			setMapStyle('light');
		}
	}, [theme, prevTheme, mapStyle]);

	const renderPopup = () => {
		if (!popupInfo) return null;

		const commonProps = {
			longitude: popupInfo.longitude,
			latitude: popupInfo.latitude,
			properties: popupInfo.properties,
			onClose: () => setPopupInfo(null)
		};

		switch (popupInfo.layerType) {
			case LayerType.Landowners:
				return <LandownerPopup {...commonProps} />;
			case LayerType.Devices:
				return <DevicePopup {...commonProps} />;
			case LayerType.Plants:
				return <PlantPopup {...commonProps} />;
			case LayerType.Ttms:
				return <TtmsPopup {...commonProps} />;
			case LayerType.EiaTransmission:
				return <EiaTransmissionPopup {...commonProps} />;
			case LayerType.EiaSubstations:
				return <EiaSubstationsPopup {...commonProps} />;
			case LayerType.PlannedPlants:
				return <PlannedPlantPopup {...commonProps} />;
			case LayerType.Loads:
				return <LoadPopup {...commonProps} />;
			default:
				return <UnknownLayerPopup layerType={popupInfo.layerType ?? ''} {...commonProps} />;
		}
	};

	const onMapLoad = async (e: MapEvent) => {
		e.target.resize();

		onStyleLoad(e);

		e.target.on('style.load', onStyleLoad);

		setupTouchListeners(e.target);

		return () => {
			e.target.off('style.load', onStyleLoad);
		};
	};

	const onStyleLoad = async (e: MapEvent) => {
		setIconsLoaded(false);
		await loadIcons(e);
	};

	const loadIcons = async (e: MapEvent) => {
		try {
			await loadMapIcons(e.target);
			setIconsLoaded(true);
		} catch (error) {
			console.error('Failed to load map icons:', error);
			toast('Failed to load map icons.');
			setIconsLoaded(true);
		}
	};

	const onFeatureSelect = (feature: FeatureResult) => {
		setPopupInfo({
			longitude: feature.longitude,
			latitude: feature.latitude,
			properties: feature.properties,
			layerType: enumValueFromString(LayerType, feature.layerType, { searchValue: true })
		});
	};

	const onMouseLeave = () => {
		setHoverInfo(null);
	};

	const onLayerToggle = (layerId: string, visible: boolean) => {
		setLayers(prev => prev.map(layer => (layer.id === layerId ? { ...layer, visible } : layer)));
	};

	const onFeatureFocus = (e: MapMouseEvent, setInfo: (value: SetStateAction<PopupInfo | null>) => void) => {
		const features = e.features;

		if (!features || features.length <= 0) {
			return;
		}

		const focusedFeature = features[0];
		let longitude: number;
		let latitude: number;

		const adjustForMultipleCopiesOfSameFeature = (coordinates: Position): Position => {
			const adjusted = [...coordinates];

			while (Math.abs(e.lngLat.lng - adjusted[0]) > 180) {
				adjusted[0] += e.lngLat.lng > adjusted[0] ? 360 : -360;
			}

			return adjusted;
		};

		if (focusedFeature.geometry.type === 'Point') {
			const coordinates = (focusedFeature.geometry as GeoJSON.Point).coordinates;
			[longitude, latitude] = adjustForMultipleCopiesOfSameFeature(coordinates);
		} else {
			longitude = e.lngLat.lng;
			latitude = e.lngLat.lat;
		}

		setInfo({
			longitude,
			latitude,
			properties: focusedFeature.properties || {},
			layerType: enumValueFromString(LayerType, focusedFeature.layer?.id ?? '', { searchValue: true })
		});

		e.preventDefault();
	};

	const onRightClick = (e: MapMouseEvent) => {
		e.preventDefault();

		const { lng, lat } = e.lngLat;

		if (mapRef.current) {
			mapRef.current.flyTo({
				center: { lng, lat },
				zoom: 16,
				duration: 1000
			});
		}

		setRightClickInfo({
			longitude: lng,
			latitude: lat,
			zoom: 16
		});
	};

	const onMapClick = () => {
		if (rightClickInfo) {
			setRightClickInfo(null);
		}
	};

	const onReset = () => {
		if (!mapRef.current) {
			return;
		}
		mapRef.current.flyTo({
			center: { lng: INITIAL_LNG, lat: INITIAL_LAT },
			zoom: INITIAL_ZOOM,
			duration: 1000,
			pitch: 0,
			bearing: 0
		});

		const url = new URL(window.location.href);
		url.searchParams.delete('lng');
		url.searchParams.delete('lat');
		url.searchParams.delete('zoom');
		window.history.replaceState({}, '', url.toString());

		setPopupInfo(null);
	};

	const transformMapRequest: RequestTransformFunction = url => {
		if (url.includes(API_SOURCE_PATH)) {
			return {
				url,
				headers: { Authorization: `Bearer ${accessToken}` }
			};
		}
		return { url };
	};

	if (!accessToken) {
		return <SkeletonCard />;
	}

	const showLandowners = layers.find(l => l.id === LayerType.Landowners)?.visible ?? false;
	const showDevices = layers.find(l => l.id === LayerType.Devices)?.visible ?? false;
	const showPlants = layers.find(l => l.id === LayerType.Plants)?.visible ?? false;
	const showTtms = layers.find(l => l.id === LayerType.Ttms)?.visible ?? false;
	const showTransmissionLines = layers.find(l => l.id === LayerType.EiaTransmission)?.visible ?? false;
	const showSubstations = layers.find(l => l.id === LayerType.EiaSubstations)?.visible ?? false;
	const showPlannedPlants = layers.find(l => l.id === LayerType.PlannedPlants)?.visible ?? false;
	const showLoads = layers.find(l => l.id === LayerType.Loads)?.visible ?? false;

	return (
		<MapGL
			ref={mapRef}
			{...viewState}
			interactiveLayerIds={[
				LayerType.Landowners,
				LayerType.Devices,
				LayerType.Plants,
				LayerType.Ttms,
				LayerType.EiaTransmission,
				LayerType.EiaSubstations,
				LayerType.PlannedPlants,
				LayerType.Loads
			]}
			onLoad={onMapLoad}
			onIdle={onHandleIdle}
			mapStyle={getMapStyleUrl(mapStyle)}
			mapboxAccessToken={MAPBOX_TOKEN}
			onMouseMove={e => onFeatureFocus(e, setHoverInfo)}
			onMouseLeave={onMouseLeave}
			onContextMenu={onRightClick}
			onClick={onMapClick}
			transformRequest={transformMapRequest}
			onMove={evt => {
				setViewState(evt.viewState);

				const { longitude, latitude, zoom } = evt.viewState;

				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				timeoutRef.current = debouncedUrlUpdate(longitude, latitude, zoom);
			}}
			onDblClick={e => onFeatureFocus(e, setPopupInfo)}
		>
			<MapTopBar viewState={viewState} onReset={onReset} onViewStateChange={setViewState}>
				<FeatureSearchControl mapRef={mapRef} onFeatureSelect={onFeatureSelect} />
				<LayerControl layers={layers} onLayerToggle={onLayerToggle} />
				<MapStyleControl currentStyle={mapStyle} onStyleChange={setMapStyle} />
			</MapTopBar>

			<MapLegend>{showTransmissionLines && <EiaTransmissionLegend />}</MapLegend>

			<GeolocateControl
				position="bottom-right"
				trackUserLocation={true}
				showUserHeading={true}
				showAccuracyCircle={true}
				style={{
					marginBottom: '16px',
					marginRight: '16px'
				}}
			/>

			<Source id={GEOMAP_SOURCE_ID} minzoom={0} type="vector" tiles={[TILE_URL]}>
				{iconsLoaded && showLandowners && <Layer {...LAYERS[LayerType.Landowners]} />}
				{iconsLoaded && showDevices && <Layer {...LAYERS[LayerType.Devices]} />}
				{iconsLoaded && showPlants && <Layer {...LAYERS[LayerType.Plants]} />}
				{iconsLoaded && showTtms && <Layer {...LAYERS[LayerType.Ttms]} />}
				{iconsLoaded && showPlannedPlants && <Layer {...LAYERS[LayerType.PlannedPlants]} />}
				{iconsLoaded && showLoads && <Layer {...LAYERS[LayerType.Loads]} />}
			</Source>

			<Source id={YES_MAPBOX_TRANSMISSION_ID} type="vector" tiles={[EIA_TRANSMISSION_TILE_URL]}>
				{showTransmissionLines && <Layer {...LAYERS[LayerType.EiaTransmission]} />}
			</Source>

			<Source id={YES_MAPBOX_SUBSTATIONS_ID} type="vector" tiles={[EIA_SUBSTATIONS_TILE_URL]}>
				{iconsLoaded && showSubstations && <Layer {...LAYERS[LayerType.EiaSubstations]} />}
			</Source>

			{popupInfo && (
				<Popup
					longitude={popupInfo.longitude}
					latitude={popupInfo.latitude}
					anchor="bottom"
					onClose={() => setPopupInfo(null)}
					closeOnClick={true}
					closeButton={false}
				>
					{renderPopup()}
				</Popup>
			)}

			{hoverInfo && !popupInfo && (
				<Popup
					longitude={hoverInfo.longitude}
					latitude={hoverInfo.latitude}
					anchor="bottom"
					closeButton={false}
					closeOnClick={false}
					className="hover-popup"
				>
					<HoverPopup
						longitude={hoverInfo.longitude}
						latitude={hoverInfo.latitude}
						properties={hoverInfo.properties}
						layerType={hoverInfo.layerType}
					/>
				</Popup>
			)}

			{rightClickInfo && (
				<Popup
					longitude={rightClickInfo.longitude}
					latitude={rightClickInfo.latitude}
					anchor="top"
					closeButton={false}
					closeOnClick={false}
					onClose={() => setRightClickInfo(null)}
					className="right-click-popup"
				>
					<div className="flex flex-col gap-2 p-2 min-w-[200px]">
						<div className="flex items-center justify-center gap-2 mt-2">
							<Link
								href={`?lat=${rightClickInfo.latitude.toFixed(6)}&lng=${rightClickInfo.longitude.toFixed(6)}&zoom=16`}
								className="text-blue-600 hover:text-blue-800 underline"
							>
								Permalink
							</Link>
						</div>
						<div className="flex items-center justify-center gap-2">
							<ViewOnGoogleMaps latitude={rightClickInfo.latitude} longitude={rightClickInfo.longitude} />
						</div>
					</div>
				</Popup>
			)}
		</MapGL>
	);
}
